// VIBRANIAM-SPACE v2 - App.jsx
// libreria moduli | viewport + grafo | export

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Viewport from './components/Viewport.jsx';
import DAGCanvas from './components/DAGCanvas.jsx';
import ExportPanel from './components/ExportPanel.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import PresetPanel from './components/PresetPanel.jsx';
import FullscreenButton from './components/FullscreenButton.jsx';
import ResizeHandle from './components/ResizeHandle.jsx';
import MobileDrawer from './components/MobileDrawer.jsx';
import useModuleGraph, { groupedNodeList } from './hooks/useModuleGraph.js';
import useAudioEngine from './hooks/useAudioEngine.js';
import useIsMobile from './hooks/useIsMobile.js';
import { kindColor } from './engine/types.js';
import { formatCount } from './utils/particle-utils.js';

const SIDEBAR_MIN = 180, SIDEBAR_MAX = 420, SIDEBAR_DEFAULT = 224;
const EXPORT_MIN = 200, EXPORT_MAX = 440, EXPORT_DEFAULT = 256;

function Meter({ label, value, color = '#4fd8ff', max = 1 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="mb-1">
      <div className="flex justify-between text-[9px] text-slate-500">
        <span>{label}</span>
        <span className="font-mono text-slate-400">{value.toFixed(3)}</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded bg-ink-900">
        <div className="h-full transition-[width] duration-75" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function Spectrum({ bands }) {
  const n = bands?.length || 0;
  if (!n) return <div className="h-9 rounded bg-ink-900/70" />;
  const w = 100 / n;
  return (
    <svg viewBox="0 0 100 36" preserveAspectRatio="none" className="h-9 w-full rounded bg-ink-900/70">
      {Array.from({ length: n }, (_, i) => {
        const h = Math.min(36, Math.sqrt(Math.max(0, bands[i]) * 14) * 36);
        return (
          <rect key={i} x={i * w + w * 0.12} y={36 - h} width={w * 0.76} height={h}
            fill={`hsl(${190 + (i / n) * 90}, 80%, ${45 + (h / 36) * 25}%)`} />
        );
      })}
    </svg>
  );
}

/** Small chevron button for a collapsible panel - desktop-only (mobile uses the drawer's own toggle instead). */
function CollapseToggle({ collapsed, onClick, axis }) {
  const rotation = axis === 'x'
    ? (collapsed ? 'rotate-0' : 'rotate-180')
    : (collapsed ? 'rotate-90' : '-rotate-90');
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? 'Espandi pannello' : 'Comprimi pannello'}
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
        className={`transition-transform ${rotation}`}>
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}

export default function App() {
  const isMobile = useIsMobile();
  const api = useModuleGraph();
  const { graph } = api;

  const analyzerCfg = useMemo(() => ({ fftSize: 2048, bands: 32, sensitivity: 1.5 }), []);
  const audio = useAudioEngine(analyzerCfg);

  const [stats, setStats] = useState({ fps: 0, alive: 0, capacity: 0, layers: 0, frame: 0, width: 0, height: 0 });
  const [liveParams, setLiveParams] = useState(null);
  const [running, setRunning] = useState(true);
  const [split, setSplit] = useState(0.5);
  const [presetOpen, setPresetOpen] = useState(false);
  const [meters, setMeters] = useState({ rms: 0, bass: 0, mid: 0, high: 0, flux: 0, bands: null });

  // Desktop panel layout: width in px for the two side panels, a height
  // fraction for the viewport/editor split (existing behaviour), and a
  // collapsed flag per panel. None of this touches the render engine - it
  // only resizes the containers the existing components already render into.
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT);
  const [exportWidth, setExportWidth] = useState(EXPORT_DEFAULT);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [exportCollapsed, setExportCollapsed] = useState(false);
  const [dagCollapsed, setDagCollapsed] = useState(false);

  const graphInputRef = useRef(null);
  const audioFileRef = useRef(null);
  const splitDragRef = useRef(false);
  const sidebarDragRef = useRef(null);
  const exportDragRef = useRef(null);

  // Bumped on error-boundary recovery to force a fully fresh mount (new WebGL
  // context, new pan/zoom state) instead of reusing whatever fiber survived.
  const [viewportKey, setViewportKey] = useState(0);
  const [dagKey, setDagKey] = useState(0);

  // Fullscreen targets.
  const appRef = useRef(null);
  const viewportWrapRef = useRef(null);

  // Pop-out projection window: a MediaStream captured straight off the
  // viewport's own <canvas>, handed to a second same-origin window. No
  // second engine instance, nothing to keep in sync - it is the same pixels.
  const viewportCanvasRef = useRef(null);
  const projectionWindowRef = useRef(null);
  const projectionStreamRef = useRef(null);
  const [projectionActive, setProjectionActive] = useState(false);

  const handleStats = useCallback((s) => {
    setStats(s);
    const f = audio.getFeatures();
    setMeters({
      rms: f.rms || 0, bass: f.bass || 0, mid: f.mid || 0,
      high: f.high || 0, flux: f.flux || 0, bands: f.bands || null,
    });
  }, [audio]);

  const handleCanvasReady = useCallback((canvas) => {
    viewportCanvasRef.current = canvas;
  }, []);

  const cleanupProjection = useCallback(() => {
    projectionStreamRef.current?.getTracks().forEach((t) => t.stop());
    projectionStreamRef.current = null;
    projectionWindowRef.current = null;
    setProjectionActive(false);
  }, []);

  const openProjection = useCallback(() => {
    const canvas = viewportCanvasRef.current;
    if (!canvas || typeof canvas.captureStream !== 'function') return;
    if (projectionWindowRef.current && !projectionWindowRef.current.closed) {
      projectionWindowRef.current.focus();
      return;
    }
    const url = `${import.meta.env.BASE_URL}projection.html`;
    const popup = window.open(url, 'vibraniam-projection', 'width=1280,height=720,menubar=no,toolbar=no,location=no');
    if (!popup) return; // blocked by the browser's popup blocker
    projectionStreamRef.current?.getTracks().forEach((t) => t.stop());
    projectionStreamRef.current = canvas.captureStream(30);
    projectionWindowRef.current = popup;
    setProjectionActive(true);

    // The popup may not have run its own script yet; retry the handoff for a
    // couple of seconds. The 'ready' message below covers the normal case,
    // this is just a safety net for a slow load.
    let tries = 0;
    const trySend = () => {
      if (popup.closed) return;
      if (typeof popup.__vsSetStream === 'function') { popup.__vsSetStream(projectionStreamRef.current); return; }
      if (++tries < 20) setTimeout(trySend, 150);
    };
    setTimeout(trySend, 150);
  }, []);

  const closeProjection = useCallback(() => {
    projectionWindowRef.current?.close();
    cleanupProjection();
  }, [cleanupProjection]);

  useEffect(() => {
    function onMessage(e) {
      if (e.origin !== window.location.origin || e.data?.source !== 'vibraniam-projection') return;
      if (e.data.type === 'ready') {
        const popup = projectionWindowRef.current;
        if (popup && !popup.closed && projectionStreamRef.current && typeof popup.__vsSetStream === 'function') {
          popup.__vsSetStream(projectionStreamRef.current);
        }
      } else if (e.data.type === 'closing') {
        cleanupProjection();
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [cleanupProjection]);

  // Some browsers/OSes let a user close a popup without ever firing
  // beforeunload in a way the opener can hear about; polling closed catches
  // that case too so the "apri proiezione" button and the captured stream
  // don't stay stuck in a stale "open" state.
  useEffect(() => {
    if (!projectionActive) return undefined;
    const id = setInterval(() => {
      if (!projectionWindowRef.current || projectionWindowRef.current.closed) cleanupProjection();
    }, 1000);
    return () => clearInterval(id);
  }, [projectionActive, cleanupProjection]);

  // Stop the captured stream and drop the popup reference if the whole app
  // unmounts (e.g. hot reload in dev) - nothing should keep pumping frames
  // into a MediaStream nobody can see.
  useEffect(() => () => {
    projectionStreamRef.current?.getTracks().forEach((t) => t.stop());
    projectionWindowRef.current?.close();
  }, []);

  const exportGraph = () => {
    const blob = new Blob([api.serialize()], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'vibraniam-space-patch.json';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const importGraph = async (file) => {
    if (!file) return;
    api.load(await file.text());
  };

  const grouped = useMemo(() => groupedNodeList(), []);

  const btnCls = isMobile
    ? 'rounded bg-ink-600 px-2.5 min-h-[44px] text-[11px] hover:bg-ink-500'
    : 'rounded bg-ink-600 px-2 py-1 text-[10px] hover:bg-ink-500';

  // Shared between the desktop header (always visible) and the top of the
  // mobile drawer (the header has no room for these on a phone width).
  const audioControls = (
    <div className={isMobile ? 'mb-3 flex flex-wrap items-center gap-1.5' : 'flex items-center gap-1.5'}>
      <button type="button" onClick={audio.startTestTone} className={btnCls}>test tone</button>
      <button type="button" onClick={audio.startMic} className={btnCls}>microfono</button>
      <button type="button" onClick={() => audioFileRef.current?.click()} className={btnCls}>file audio (live)</button>
      <input ref={audioFileRef} type="file" accept="audio/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) audio.startFile(f); e.target.value = ''; }} />
      <button type="button" onClick={audio.startCamera} className={btnCls}>camera</button>
      <span className={`truncate text-[10px] text-slate-500 ${isMobile ? 'w-full' : 'ml-1 w-40'}`} title={audio.sourceLabel}>
        {audio.error ? <span className="text-red-400">{audio.error}</span> : audio.sourceLabel}
      </span>
    </div>
  );

  const sidebarContent = (
    <>
      <h2 className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-300">Analisi live</h2>
      <Spectrum bands={meters.bands} />
      <div className="mt-1.5">
        <Meter label="RMS"  value={meters.rms}  max={0.5}  color="#8ef07a" />
        <Meter label="Bass" value={meters.bass} max={0.15} color="#ffb454" />
        <Meter label="Mid"  value={meters.mid}  max={0.10} color="#4fd8ff" />
        <Meter label="High" value={meters.high} max={0.05} color="#a97bff" />
        <Meter label="Flux" value={meters.flux} max={0.02} color="#ff7ba9" />
      </div>

      <h2 className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-widest text-slate-300">Moduli</h2>
      {grouped.map(([group, mods]) => (
        <div key={group} className="mb-2.5">
          <div className="mb-1 text-[9px] uppercase tracking-widest text-slate-600">{group}</div>
          {mods.map((m) => (
            <div
              key={m.type}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/x-vs-module', m.type);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              onDoubleClick={() => api.addNode(m.type, 100, 100)}
              title={m.hint || 'Trascina sul canvas (o doppio click)'}
              className={`mb-1 cursor-grab rounded border border-white/5 bg-ink-700 px-2 transition hover:border-white/20 hover:bg-ink-600 ${
                isMobile ? 'min-h-[44px] py-2 text-[12px]' : 'py-1.5 text-[10px]'}`}
            >
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm" style={{ background: m.accent }} />
                <span className="truncate text-slate-200">{m.title}</span>
              </div>
              <div className="mt-0.5 flex gap-1 pl-3.5">
                {(m.outputs || []).map((o) => (
                  <span key={o.id} className="rounded px-1 text-[8px] uppercase tracking-wide"
                    style={{ color: kindColor(o.kind), background: `${kindColor(o.kind)}1a` }}>
                    {o.kind}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      <div className="mt-2 space-y-1">
        <button type="button" onClick={() => setPresetOpen(true)}
          className={`w-full font-semibold text-ink-900 ${isMobile
            ? 'min-h-[44px] rounded bg-vibe-cyan/90 px-2.5 text-[12px] hover:brightness-110'
            : 'rounded bg-vibe-cyan/90 px-2 py-1 text-[10px] hover:brightness-110'}`}>preset...</button>
        <button type="button" onClick={exportGraph} className={`w-full ${btnCls}`}>salva patch (.json)</button>
        <button type="button" onClick={() => graphInputRef.current?.click()} className={`w-full ${btnCls}`}>carica patch</button>
        <input ref={graphInputRef} type="file" accept="application/json" className="hidden"
          onChange={(e) => importGraph(e.target.files?.[0])} />
        <button type="button" onClick={api.reset}
          className={`w-full rounded border border-white/10 text-slate-400 hover:bg-white/5 ${
            isMobile ? 'min-h-[44px] px-2.5 text-[12px]' : 'px-2 py-1 text-[10px]'}`}>patch di default</button>
      </div>

      {api.issues.length > 0 && (
        <div className="mt-3 rounded border border-amber-500/30 bg-amber-950/30 p-1.5 text-[9px] leading-snug text-amber-300/90">
          {api.issues.slice(0, 4).map((i) => <div key={i}>· {i}</div>)}
          {api.issues.length > 4 && <div>· +{api.issues.length - 4} altri</div>}
        </div>
      )}
    </>
  );

  const dagPanel = (
    <ErrorBoundary label="Editor moduli" onReset={() => setDagKey((k) => k + 1)}>
      <DAGCanvas key={dagKey} graph={graph} api={api} liveParams={liveParams} onDropModule={api.addNode} />
    </ErrorBoundary>
  );

  const exportPanel = <ExportPanel graph={graph} api={api} />;

  const viewportPanel = (
    <div ref={viewportWrapRef} className="relative min-h-0 h-full">
      <ErrorBoundary label="Viewport 3D" onReset={() => setViewportKey((k) => k + 1)}>
        <Viewport
          key={viewportKey}
          graph={graph}
          audio={audio}
          running={running}
          onStats={handleStats}
          onLiveParams={setLiveParams}
          onContextLost={() => setViewportKey((k) => k + 1)}
          onCanvasReady={handleCanvasReady}
        />
      </ErrorBoundary>
      <div className="pointer-events-none absolute left-3 top-3 rounded border border-white/10 bg-ink-900/70 px-2 py-1 font-mono text-[10px] text-slate-400 backdrop-blur">
        {stats.fps.toFixed(0)} fps · {formatCount(stats.alive)}/{formatCount(stats.capacity)} particelle
        · {stats.layers} layer · {stats.width}×{stats.height} · frame {stats.frame}
      </div>
      <div className="absolute right-3 top-3 flex items-center gap-1.5">
        {/* Pop-out projector window needs a second monitor to drag to - not a mobile use case, and window.open() just opens a mobile browser tab instead. */}
        {!isMobile && (
          <button type="button" onClick={projectionActive ? closeProjection : openProjection}
            title={projectionActive ? 'Chiudi la finestra di proiezione' : 'Apri in finestra separata (proiettore)'}
            className={`rounded px-2 py-1 text-[10px] font-semibold backdrop-blur ${
              projectionActive ? 'bg-vibe-lime text-ink-900' : 'bg-ink-900/70 text-slate-300 hover:bg-ink-700'}`}>
            {projectionActive ? 'proiezione attiva ×' : 'finestra separata'}
          </button>
        )}
        <FullscreenButton targetRef={viewportWrapRef} touchSize={isMobile} className="bg-ink-900/70 backdrop-blur hover:bg-ink-700" />
      </div>
    </div>
  );

  return (
    <div
      ref={appRef}
      className="flex h-screen w-screen flex-col bg-ink-900 text-slate-200"
      onPointerMove={(e) => {
        if (splitDragRef.current) {
          const host = e.currentTarget.querySelector('[data-centre]');
          if (!host) return;
          const rect = host.getBoundingClientRect();
          setSplit(Math.max(0.2, Math.min(0.85, (e.clientY - rect.top) / rect.height)));
          return;
        }
        if (sidebarDragRef.current) {
          const { startX, startWidth } = sidebarDragRef.current;
          setSidebarWidth(Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, startWidth + (e.clientX - startX))));
          return;
        }
        if (exportDragRef.current) {
          const { startX, startWidth } = exportDragRef.current;
          setExportWidth(Math.max(EXPORT_MIN, Math.min(EXPORT_MAX, startWidth - (e.clientX - startX))));
        }
      }}
      onPointerUp={() => {
        splitDragRef.current = false;
        sidebarDragRef.current = null;
        exportDragRef.current = null;
      }}
    >
      <header className="flex h-11 shrink-0 items-center justify-between border-b border-white/10 bg-ink-800/80 px-3">
        <div className="flex items-center gap-2.5">
          <a
            href="/"
            title="Torna a NOVA Synth Studio"
            className="flex items-center gap-2 rounded px-1 py-1 transition hover:bg-white/5"
          >
            {/* Il logo sta nella radice del sito; nella build isolata non c'e,
                quindi qui si usa un pixel trasparente e il link resta solo testo. */}
            <img
              src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
              alt=""
              className="h-5 w-auto shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span className="text-[10px] uppercase tracking-wider text-slate-500 transition hover:text-slate-200">
              Home
            </span>
          </a>
          <span className="h-4 w-px bg-white/10" />
          <span className="text-[13px] font-bold tracking-[0.18em] text-vibe-cyan">VIBRANIAM</span>
          {!isMobile && <span className="text-[13px] font-light tracking-[0.18em] text-slate-400">SPACE</span>}
          {!isMobile && <span className="ml-1 rounded bg-ink-600 px-1.5 py-0.5 font-mono text-[9px] text-slate-400">engine v2</span>}
        </div>

        <div className="flex items-center gap-1.5">
          {!isMobile && audioControls}
          <button type="button" onClick={() => setRunning((r) => !r)}
            className={`rounded font-semibold ${isMobile ? 'min-h-[44px] px-2.5 text-[11px]' : 'px-2 py-1 text-[10px]'} ${
              running ? 'bg-vibe-lime text-ink-900' : 'bg-ink-600 text-slate-300'}`}>
            {running ? 'running' : 'paused'}
          </button>
          <FullscreenButton targetRef={appRef} touchSize={isMobile} />
        </div>
      </header>

      {isMobile ? (
        <>
          <div className="min-h-0 flex-1">{viewportPanel}</div>
          <MobileDrawer>
            <div className="p-2.5">
              {audioControls}
              {sidebarContent}
            </div>
            <div className="mt-2 h-[50vh] border-t border-white/10">{dagPanel}</div>
            <div className="mt-2 border-t border-white/10">{exportPanel}</div>
          </MobileDrawer>
        </>
      ) : (
        <div className="flex min-h-0 flex-1">
          {sidebarCollapsed ? (
            <div className="flex w-7 shrink-0 flex-col items-center border-r border-white/10 bg-ink-800/70 pt-2">
              <CollapseToggle collapsed axis="x" onClick={() => setSidebarCollapsed(false)} />
            </div>
          ) : (
            <>
              <aside style={{ width: sidebarWidth }} className="flex shrink-0 flex-col overflow-y-auto border-r border-white/10 bg-ink-800/70 p-2.5">
                <div className="mb-1 flex justify-end">
                  <CollapseToggle axis="x" onClick={() => setSidebarCollapsed(true)} />
                </div>
                {sidebarContent}
              </aside>
              <ResizeHandle orientation="vertical" onPointerDown={(e) => { sidebarDragRef.current = { startX: e.clientX, startWidth: sidebarWidth }; }} />
            </>
          )}

          <main data-centre className="flex min-w-0 flex-1 flex-col">
            <div className="relative min-h-0" style={{ height: dagCollapsed ? 'calc(100% - 34px)' : `${split * 100}%` }}>
              {viewportPanel}
            </div>

            {dagCollapsed ? (
              <div className="flex h-[34px] shrink-0 items-center justify-center gap-2 border-t border-white/10 bg-ink-800/70">
                <CollapseToggle collapsed axis="y" onClick={() => setDagCollapsed(false)} />
                <span className="text-[9px] uppercase tracking-widest text-slate-500">editor moduli</span>
              </div>
            ) : (
              <>
                <ResizeHandle orientation="horizontal" onPointerDown={() => { splitDragRef.current = true; }} />
                <div className="flex h-6 shrink-0 items-center justify-between border-b border-white/10 bg-ink-800/50 px-2">
                  <span className="text-[9px] uppercase tracking-widest text-slate-500">editor moduli</span>
                  <CollapseToggle axis="y" onClick={() => setDagCollapsed(true)} />
                </div>
                <div className="min-h-0 flex-1">{dagPanel}</div>
              </>
            )}
          </main>

          {exportCollapsed ? (
            <div className="flex w-7 shrink-0 flex-col items-center border-l border-white/10 bg-ink-800/70 pt-2">
              <CollapseToggle collapsed axis="x" onClick={() => setExportCollapsed(false)} />
            </div>
          ) : (
            <>
              <ResizeHandle orientation="vertical" onPointerDown={(e) => { exportDragRef.current = { startX: e.clientX, startWidth: exportWidth }; }} />
              <div style={{ width: exportWidth }} className="flex shrink-0 flex-col">
                <div className="flex justify-start border-b border-white/10 bg-ink-800/70 px-1">
                  <CollapseToggle axis="x" onClick={() => setExportCollapsed(true)} />
                </div>
                <div className="min-h-0 flex-1">{exportPanel}</div>
              </div>
            </>
          )}
        </div>
      )}

      <PresetPanel
        open={presetOpen}
        onClose={() => setPresetOpen(false)}
        graph={graph}
        onLoad={api.load}
      />

      {api.strobePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="max-w-md rounded-lg border border-amber-500/40 bg-ink-800 p-4 text-slate-200">
            <h3 className="mb-2 text-[13px] font-semibold text-amber-300">Avviso di fotosensibilità</h3>
            <p className="mb-3 text-[11px] leading-relaxed text-slate-300">
              Questo modulo alterna immagini a lampi rapidi (stroboscopio). Se soffri di epilessia
              fotosensibile o di disturbi legati a luci intermittenti, evita di usarlo. Confermi di
              voler attivare l&apos;effetto stroboscopico?
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={api.dismissStrobe}
                className="rounded bg-ink-600 px-3 py-1.5 text-[11px] text-slate-300 hover:bg-ink-500">
                Non attivare
              </button>
              <button type="button" onClick={() => api.confirmStrobe(api.strobePrompt)}
                className="rounded bg-vibe-cyan px-3 py-1.5 text-[11px] font-semibold text-ink-900 hover:brightness-110">
                Confermo e attivo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
